/**
 * Main Customer View - QR code and SMS registration on one page
 *
 * Customers can either:
 * 1. Scan the QR code shown directly on this page
 * 2. Enter their phone number to start SMS-based queue registration
 *    (continues to service selection on the SMS page)
 */
import React, { useState } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    useTheme,
    useMediaQuery,
    Card,
    CardContent,
    CircularProgress,
    Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { validatePhoneNumber } from "../../../api/api";
import { normalizePhone } from "../../../utils/phone";
import ScrollButtons from "../../../components/ScrollButtons";
import useBranchInfo from "../../../hooks/useBranchInfo";
import BranchHeader from "./BranchHeader";
import BranchNotFound, { BranchLoading } from "./BranchNotFound";
import CustomerFooter from "./CustomerFooter";
import useCustomerFlowTexts from "./useCustomerFlowTexts";

const CustomerChoicePage = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const {
        branchId,
        branchName,
        logoUrl,
        qrDataUrl,
        smsEnabled,
        loading,
        error,
        withBranch,
    } = useBranchInfo();
    const texts = useCustomerFlowTexts(branchId);

    const [phoneNumber, setPhoneNumber] = useState("");
    const [phoneLoading, setPhoneLoading] = useState(false);
    const [phoneError, setPhoneError] = useState(null);

    const handlePhoneSubmit = async () => {
        if (!phoneNumber.trim()) {
            setPhoneError("Vennligst skriv inn telefonnummer.");
            return;
        }

        // The SMS operator drops numbers without a country code, so carry the
        // normalized form through the whole flow.
        const msisdn = normalizePhone(phoneNumber);
        if (!msisdn) {
            setPhoneError("Ugyldig telefonnummer. Skriv f.eks. +47 123 45 678.");
            return;
        }

        setPhoneLoading(true);
        setPhoneError(null);

        try {
            const response = await validatePhoneNumber(msisdn, branchId);
            navigate(withBranch("/home/sms-customer-view"), {
                state: {
                    phoneNumber: msisdn,
                    customer: response.customer,
                },
            });
        } catch (err) {
            console.error("Phone validation failed", err);
            setPhoneError("Ugyldig telefonnummer. Prøv igjen.");
            setPhoneLoading(false);
        }
    };

    if (loading) return <BranchLoading />;
    if (error) return <BranchNotFound />;

    return (
        <>
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                px={2}
                sx={{ mt: { xs: 6, sm: 10 } }}
            >
                <BranchHeader
                    logoUrl={logoUrl}
                    branchName={branchName}
                    isMobile={isMobile}
                />

                <Typography
                    variant="h4"
                    color="primary"
                    textAlign="center"
                    mb={2}
                    sx={{ fontWeight: "bold" }}
                >
                    Velkommen til {branchName}!
                </Typography>

                <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    textAlign="center"
                    mb={4}
                    sx={{ maxWidth: 500 }}
                >
                    {smsEnabled
                        ? "Velg hvordan du vil registrere deg:"
                        : "Skann QR-koden for å registrere deg i køen:"}
                </Typography>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        gap: 3,
                        width: "100%",
                        maxWidth: 600,
                        justifyContent: "center",
                    }}
                >
                    {/* QR Code Option */}
                    <Card
                        sx={{
                            flex: 1,
                            minHeight: 200,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                        }}
                    >
                        <CardContent>
                            <Typography
                                variant="h6"
                                color="primary"
                                textAlign="center"
                                mb={1}
                                sx={{ fontWeight: "bold" }}
                            >
                                📱 QR-kode
                            </Typography>
                            <Typography
                                variant="subtitle1"
                                color="text.secondary"
                                textAlign="center"
                                mb={2}
                            >
                                {texts.qr_scan_text}
                            </Typography>
                            {qrDataUrl ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                    }}
                                >
                                    <img
                                        src={qrDataUrl}
                                        alt="QR Code"
                                        style={{
                                            width: isMobile ? 180 : 200,
                                            height: isMobile ? 180 : 200,
                                            maxWidth: "100%",
                                            border: "1px solid #e0e0e0",
                                            borderRadius: "8px",
                                        }}
                                    />
                                </Box>
                            ) : (
                                <Alert severity="warning">
                                    Ingen QR-kode tilgjengelig
                                </Alert>
                            )}
                        </CardContent>
                    </Card>

                    {/* SMS Option - only for branches that opted in */}
                    {smsEnabled && (
                    <Card
                        sx={{
                            flex: 1,
                            minHeight: 200,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                        }}
                    >
                        <CardContent>
                            <Typography
                                variant="h6"
                                color="primary"
                                textAlign="center"
                                mb={1}
                                sx={{ fontWeight: "bold" }}
                            >
                                💬 SMS
                            </Typography>
                            <Typography
                                variant="subtitle1"
                                color="text.secondary"
                                textAlign="center"
                                mb={2}
                            >
                                {texts.sms_phone_text}
                            </Typography>
                            <TextField
                                fullWidth
                                label="Mobilnummer"
                                value={phoneNumber}
                                onChange={(e) =>
                                    setPhoneNumber(e.target.value)
                                }
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !phoneLoading) {
                                        handlePhoneSubmit();
                                    }
                                }}
                                placeholder="+47 123 45 678"
                                variant="outlined"
                                error={!!phoneError}
                                helperText={phoneError}
                                sx={{ mb: 2 }}
                            />
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={handlePhoneSubmit}
                                fullWidth
                                disabled={phoneLoading || !phoneNumber.trim()}
                            >
                                {phoneLoading ? (
                                    <CircularProgress size={24} />
                                ) : (
                                    "Fortsett"
                                )}
                            </Button>
                        </CardContent>
                    </Card>
                    )}
                </Box>

                <CustomerFooter />
            </Box>
            <ScrollButtons />
        </>
    );
};

export default CustomerChoicePage;
